import { SupernovaException } from "./errors";
export function forceUnwrapNullish(value) {
    if (value === null)
        throw new Error("Illegal null");
    if (value === undefined)
        throw new Error("Illegal undefined");
    return value;
}
export function trimLeadingSlash(string) {
    return string.startsWith("/") ? string.substring(1) : string;
}
export function trimTrailingSlash(string) {
    return string.endsWith("/") ? string.substring(0, string.length - 1) : string;
}
export function tryParseUrl(url) {
    try {
        return parseUrl(url);
    }
    catch (e) {
        console.error(`Error parsing URL ${url}`);
        console.error(e);
        return null;
    }
}
export function parseUrl(url) {
    return new URL(url.startsWith("https://") || url.startsWith("http://")
        ? url
        : `https://${url}`);
}
export function mapByUnique(items, keyFn) {
    const result = new Map();
    for (const item of items) {
        result.set(keyFn(item), item);
    }
    return result;
}
export function groupBy(items, keyFn) {
    const result = new Map();
    for (const item of items) {
        const key = keyFn(item);
        const array = result.get(key);
        if (array) {
            array.push(item);
        }
        else {
            result.set(key, [item]);
        }
    }
    return result;
}
export function filterNonNullish(items) {
    return items.filter(Boolean);
}
export function nonNullishFilter(item) {
    return !!item;
}
export function nonNullFilter(item) {
    return item !== null;
}
export function buildConstantEnum(values) {
    const constMap = values.reduce((acc, code) => ({ ...acc, [code]: code }), {});
    return constMap;
}
export async function promiseWithTimeout(timeoutMs, promise, onTimeout) {
    let timeoutHandle;
    const timeoutPromise = new Promise((_, reject) => {
        timeoutHandle = setTimeout(() => { var _a; return reject((_a = onTimeout === null || onTimeout === void 0 ? void 0 : onTimeout()) !== null && _a !== void 0 ? _a : SupernovaException.timeout()); }, timeoutMs);
    });
    const result = await Promise.race([promise(), timeoutPromise]);
    clearTimeout(timeoutHandle);
    return result;
}
export async function sleep(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}
export function uniqueBy(items, prop) {
    return Array.from(mapByUnique(items, prop).values());
}
export function areShallowObjectsEqual(lhs, rhs) {
    if ((lhs === undefined) !== (rhs === undefined))
        return false;
    if (lhs === undefined || rhs === undefined)
        return true;
    if ((lhs === null) !== (rhs === null))
        return false;
    if (lhs === null || rhs === null)
        return true;
    for (const key in lhs) {
        if (!(key in rhs) || lhs[key] !== rhs[key]) {
            return false;
        }
    }
    for (const key in rhs) {
        if (!(key in lhs)) {
            return false;
        }
    }
    return true;
}
export function isNotNullish(value) {
    return !isNullish(value);
}
export function isNullish(value) {
    return value === null || value === undefined;
}
export function hasProperty(prop) {
    return (obj) => {
        return obj[prop] !== null && obj[prop] !== undefined;
    };
}
export function hasTruthyProperty(prop) {
    return (obj) => {
        return !!obj[prop];
    };
}
export function chunkedArray(array, chunkSize) {
    const result = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        result.push(array.slice(i, i + chunkSize));
    }
    return result;
}
export function recordToMap(record) {
    const map = new Map();
    for (const [k, v] of Object.entries(record))
        map.set(k, v);
    return map;
}
/**
 * Applies direct properties from `update` to `object`.
 *
 *  - In case a property is not defined in `update`, it `object` will retain the current one
 *  - In case a property is null in `update`, it will be removed from `object`
 *
 * @param object base object to perform updates on
 * @param update source of property updates
 * @returns object by merging updates from `update` into `object`
 */
export function applyShallowObjectUpdate(object, update) {
    const objectShallowCopy = { ...object };
    for (const [key, value] of Object.entries(update)) {
        if (value === null) {
            // @ts-expect-error it's very hard to get typescript to believe that the types will match
            objectShallowCopy[key] = undefined;
        }
        else if (value !== undefined) {
            // @ts-expect-error it's very hard to get typescript to believe that the types will match
            objectShallowCopy[key] = value;
        }
    }
    return objectShallowCopy;
}
/**
 * Returns a shallow copy of the input object with only properties that are not `undefined`.
 *
 * @param obj - The input object whose properties may include `undefined` values.
 * @returns A new object containing only the properties from the original object that have defined (non-undefined) values.
 */
export function pickDefined(obj) {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
}
